import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, API, idr } from "../lib/api";
import {
  Download, ArrowDownRight, ArrowLeftRight, CheckCircle2, AlertTriangle, Info,
  RefreshCw, ShoppingBag, FileText, X, Phone, Building, Scale, Clock, Settings2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { DatePicker } from "@/components/ui/date-picker";
import { hariIniISO } from "@/lib/tanggal";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

// Nomor tampilan kosmetik untuk dokumen yang belum punya sistem penomoran
// formal — SAMA PERSIS `_no_faktur()` di backend/routers/faktur.py. Diduplikasi
// (bukan diimpor) karena berkas bersama (`lib/api.js`) di luar cakupan berkas
// yang boleh disentuh untuk brief ini — lihat duplikat sama di RekonSales.jsx.
const noInvoice = (id) => `INV-${(id || "").slice(0, 8).toUpperCase()}`;

export default function RekonAkuisisi() {
  const { user, can } = useAuth();
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const canWrite = ["superadmin", "admin", "kasir_pos"].includes(user?.role);
  const canAdjust = ["superadmin", "admin"].includes(user?.role);
  // Verifikasi fisik trade-in dipagari izin BACKEND yang sama persis:
  // POST .../trade-in/{sale_id}/{index}/terima|batal keduanya memakai
  // require_permission("acquisitions", "update"). Tombolnya hanya muncul
  // kalau endpointnya benar-benar akan menerima, bukan berujung 403.
  const canVerifTradeIn = can("acquisitions", "update");
  // Gate tautan nomor invoice (poin C brief bos 14 Agu) — pola sama dengan
  // Pelanggan.jsx: tautan hanya tampil kalau pengguna berhak melihat modul
  // tujuannya, supaya tidak ada tautan yang ujungnya 403/halaman kosong.
  const bolehLihatAkuisisi = can("acquisitions", "view");
  const bolehLihatSales = can("sales", "view");
  // Saldo penampung trade-in + penutupan selisih dipagari kunci modul yang SAMA
  // PERSIS dengan backend (GET .../saldo-penampung = finance_rekonsiliasi/view,
  // POST .../selisih-penilaian = finance_rekonsiliasi/create). Sengaja BUKAN
  // `acquisitions` seperti tombol terima/batal di halaman ini: menandai barang
  // diterima itu pekerjaan gudang, menulis jurnal penyesuaian bukan.
  const bolehLihatSaldoTI = can("finance_rekonsiliasi", "view");
  const bolehTutupSelisih = can("finance_rekonsiliasi", "create");
  const [date, setDate] = useState(hariIniISO());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branch, setBranch] = useState("");
  const [items, setItems] = useState([]);
  const [totalOut, setTotalOut] = useState(0);
  const [tiSummary, setTiSummary] = useState({
    n_trade_in_unit: 0, n_trade_in_belum_diverifikasi: 0,
    n_trade_in_diterima: 0, total_nilai_trade_in: 0,
  });
  const [coa, setCoa] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actualDraft, setActualDraft] = useState({});
  const [adjDraft, setAdjDraft] = useState({});
  const [adjNoteDraft, setAdjNoteDraft] = useState({});
  const [txActualDraft, setTxActualDraft] = useState({});
  // Detail per transaksi (poin C brief bos 14 Agu) — dua bentuk sumber:
  // "akuisisi" (fetch penuh /acquisitions/{id}, sama pola Acquisitions.jsx)
  // dan "trade_in" (baris baca-lewat dari sales, datanya SUDAH lengkap di
  // baris listing — tidak perlu GET tambahan).
  const [detail, setDetail] = useState(null);
  // Saldo berjalan tiap akun penampung trade-in cabang; `selisihDraft.akun_id`
  // menandai baris mana yang formnya sedang terbuka (satu saja pada satu waktu).
  const [saldoTI, setSaldoTI] = useState(null);
  // Daftar tunggu = barang trade-in yang sudah diterima dari pelanggan tapi
  // belum masuk stok lewat PO. Saldo penampung menjawab "berapa"; ini menjawab
  // "yang mana, milik siapa, sudah berapa lama".
  const [tunggu, setTunggu] = useState(null);
  const [hanyaTerlambat, setHanyaTerlambat] = useState(false);
  const [ambangDraft, setAmbangDraft] = useState("");
  const [editAmbang, setEditAmbang] = useState(false);
  const [selisihDraft, setSelisihDraft] = useState({ akun_id: "", nominal: "", alasan: "" });
  const [busySelisih, setBusySelisih] = useState(false);
  const isRange = !!(dateFrom || dateTo);
  // Pengurutan tabel (client-side, lihat components/ui/sort.jsx) — urutan
  // bawaan tetap seperti sebelumnya.
  const coaSort = useSortState(null, "asc");
  const txSort = useSortState(null, "asc");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const p = new URLSearchParams();
      if (isRange) {
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
      } else {
        p.set("date", date);
      }
      if (branch) p.set("branch", branch);
      const list = await api.get(`/rekon-akuisisi?${p}`);
      setItems(list.data.items || []);
      setTotalOut(list.data.total_outgoing || 0);
      setTiSummary({
        n_trade_in_unit: list.data.n_trade_in_unit || 0,
        n_trade_in_belum_diverifikasi: list.data.n_trade_in_belum_diverifikasi || 0,
        n_trade_in_diterima: list.data.n_trade_in_diterima || 0,
        total_nilai_trade_in: list.data.total_nilai_trade_in || 0,
      });
      const td = {};
      (list.data.items || []).forEach(x => { if (x.actual_value != null) td[x.tx_key] = x.actual_value; });
      setTxActualDraft(td);
      if (!isRange) {
        const rec = await api.get(`/coa-recon/akuisisi?${p}`);
        setCoa(rec.data);
        const draft = {}, adj = {}, adjN = {};
        (rec.data.rows || []).forEach(r => {
          draft[r.channel] = r.actual;
          adj[r.channel] = r.adjustment || 0;
          adjN[r.channel] = r.adjustment_note || "";
        });
        setActualDraft(draft); setAdjDraft(adj); setAdjNoteDraft(adjN);
      } else {
        setCoa(null); setActualDraft({}); setAdjDraft({}); setAdjNoteDraft({});
      }
    } finally { setBusy(false); }
  }, [date, dateFrom, dateTo, branch, isRange]);
  useEffect(() => { load(); }, [load]);

  // Saldo penampung sengaja dimuat TERPISAH dari `load()`: ia saldo berjalan
  // (tidak ikut filter tanggal/cabang), dan kalau digabung, satu 403 bagi
  // pengguna tanpa izin rekonsiliasi akan menjatuhkan seluruh isi halaman.
  const loadSaldoTI = useCallback(async () => {
    if (!bolehLihatSaldoTI) return;
    try {
      const r = await api.get("/rekon-akuisisi/trade-in/saldo-penampung");
      setSaldoTI(r.data);
    } catch {
      setSaldoTI(null);  // panel hilang, sisa halaman tetap utuh
    }
  }, [bolehLihatSaldoTI]);
  useEffect(() => { loadSaldoTI(); }, [loadSaldoTI]);

  // Terpisah dari `load()` karena tidak ikut filter tanggal halaman ini: daftar
  // tunggu selalu tentang barang yang MASIH menggantung hari ini, bukan tentang
  // periode yang sedang dilihat.
  const loadTunggu = useCallback(async () => {
    if (!bolehLihatAkuisisi) return;
    try {
      const p = new URLSearchParams();
      if (branch) p.set("branch", branch);
      if (hanyaTerlambat) p.set("hanya_terlambat", "true");
      const r = await api.get(`/rekon-akuisisi/trade-in/daftar-tunggu?${p}`);
      setTunggu(r.data);
      setAmbangDraft(String(r.data?.ambang_hari ?? ""));
    } catch {
      setTunggu(null);  // panel hilang, sisa halaman tetap utuh
    }
  }, [bolehLihatAkuisisi, branch, hanyaTerlambat]);
  useEffect(() => { loadTunggu(); }, [loadTunggu]);

  const simpanAmbang = async () => {
    const n = parseInt(ambangDraft, 10);
    if (Number.isNaN(n) || n < 0) { toast.error("Isi jumlah hari (0 atau lebih)"); return; }
    try {
      const r = await api.patch("/rekon-akuisisi/trade-in/pengaturan-ambang", { ambang_hari: n });
      toast.success(r.data?.message || "Ambang tersimpan");
      setEditAmbang(false);
      loadTunggu();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan ambang"); }
  };

  const simpanSelisih = async (row) => {
    const nominal = parseFloat(selisihDraft.nominal) || 0;
    const alasan = (selisihDraft.alasan || "").trim();
    if (nominal <= 0) { toast.error("Nominal harus lebih dari 0"); return; }
    if (alasan.length < 10) {
      toast.error("Alasan wajib diisi dan harus menjelaskan (minimal 10 karakter)");
      return;
    }
    // Konfirmasi menyebut ARAH dan NOMINAL, bukan cuma "yakin?" — ini menulis
    // jurnal ke buku besar dan tidak ada tombol undo.
    const arah = row.saldo > 0 ? "kerugian (beban)" : "keuntungan";
    if (!window.confirm(
      `Tutup ${idr(nominal)} dari ${row.name} ke akun 6195 Selisih Penilaian ` +
      `sebagai ${arah}?\n\nJurnal langsung masuk buku besar dan tidak bisa dibatalkan.`
    )) return;
    setBusySelisih(true);
    try {
      const r = await api.post("/rekon-akuisisi/trade-in/selisih-penilaian", {
        akun_id: row.akun_id, nominal, alasan,
      });
      toast.success(r.data?.sudah_nol
        ? `${row.name} sudah nol — semua barang trade-in sudah terhitung`
        : `Tersimpan. Sisa saldo ${idr(r.data?.saldo_sesudah || 0)}`);
      setSelisihDraft({ akun_id: "", nominal: "", alasan: "" });
      loadSaldoTI();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menutup selisih");
    } finally { setBusySelisih(false); }
  };

  const saveActual = async (channel) => {
    try {
      await api.post("/rekon-actuals", {
        date, branch, type: "akuisisi", channel,
        actual_amount: parseFloat(actualDraft[channel]) || 0,
        adjustment_amount: parseFloat(adjDraft[channel]) || 0,
        adjustment_note: adjNoteDraft[channel] || "",
      });
      toast.success(`${channel} tersimpan`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const saveTxActual = async (txKey) => {
    try {
      await api.post("/rekon-tx-actuals", {
        tx_key: txKey,
        actual_value: parseFloat(txActualDraft[txKey]) || 0,
      });
      toast.success("Nilai aktual tersimpan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Buka detail satu transaksi (poin C brief bos 14 Agu). Baris trade-in
  // sudah bawa semua field yang dibutuhkan dari listing (baca-lewat dari
  // sales) — tampilkan langsung tanpa GET tambahan. Baris akuisisi biasa
  // hanya bawa field ringkas di listing, jadi diperkaya lewat GET
  // /acquisitions/{id} (field: no_hp/no_rekening/nama_bank/catatan/checklist/dst,
  // sama seperti detail modal Acquisitions.jsx).
  const openDetail = async (a) => {
    if (a.sumber === "trade_in") {
      setDetail({ ...a, _tipe: "trade_in" });
      return;
    }
    setDetail({ _tipe: "akuisisi", _loading: true, id: a.id });
    try {
      const { data } = await api.get(`/acquisitions/${a.id}`);
      setDetail({ ...data, _tipe: "akuisisi" });
    } catch {
      toast.error("Gagal memuat detail");
      setDetail(null);
    }
  };

  const terimaTradeIn = async (a) => {
    if (!window.confirm(`Tandai unit trade-in "${a.nama_unit}" dari ${a.nama_pelanggan} sudah diterima gudang?`)) return;
    try {
      await api.post(`/rekon-akuisisi/trade-in/${a.sale_id}/${a.trade_in_index}/terima`, {});
      toast.success("Unit trade-in ditandai diterima");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menandai diterima"); }
  };

  const batalTradeIn = async (a) => {
    const alasan = (window.prompt(`Alasan membatalkan penandaan "diterima" untuk "${a.nama_unit}" (wajib diisi):`) || "").trim();
    if (!alasan) return;
    try {
      await api.post(`/rekon-akuisisi/trade-in/${a.sale_id}/${a.trade_in_index}/batal`, { alasan });
      toast.success("Penandaan 'diterima' dibatalkan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); }
  };

  const exportCoa = async () => {
    const token = localStorage.getItem("hris_token");
    const p = new URLSearchParams({ date });
    if (branch) p.set("branch", branch);
    const res = await fetch(`${API}/coa-recon/akuisisi/export?${p}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Gagal export"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `COA_RekonAkuisisi_${date}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportDaily = async () => {
    const token = localStorage.getItem("hris_token");
    const periode = date.slice(0, 7);
    const p = new URLSearchParams({ periode });
    if (branch) p.set("branch", branch);
    const res = await fetch(`${API}/export/rekon-akuisisi-daily?${p}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Gagal export"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `RekonAkuisisi_Daily_${periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Export per-hari (31 sheet) sedang diunduh");
  };

  const activeCoa = useMemo(() => (coa?.rows || []).filter(r => r.expected > 0 || r.actual > 0), [coa]);

  return (
    <div className="p-4 md:p-8 max-w-[1600px]" data-testid="rekon-akuisisi-page">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-violet-700 mb-2">Rekon Akuisisi</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Rekonsiliasi Akuisisi Harian</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-3xl">
            <Info className="w-3.5 h-3.5 inline mb-0.5 mr-1" />
            Data akuisisi unit yang sudah disetujui + rekap outgoing per COA. Isi nominal keluar aktual per channel untuk cek selisih.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCoa} data-testid="btn-export-coa-akq"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium">
            <Download className="w-4 h-4" /> Export COA Excel
          </button>
          <button onClick={exportDaily} data-testid="btn-export-rekon-akq-daily"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-800 text-sm font-medium">
            <Download className="w-4 h-4" /> Export Bulanan (Per Sheet)
          </button>
        </div>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4 flex flex-wrap items-end gap-3">
        <label><span className="text-xs font-medium text-slate-600">Tanggal</span>
          <DatePicker value={date} onChange={v => { setDate(v); setDateFrom(""); setDateTo(""); }}
            data-testid="rekon-akq-date"
            className="block mt-1" />
        </label>
        <label><span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <DatePicker value={dateFrom} onChange={setDateFrom} data-testid="rekon-akq-date-from"
            className="block mt-1" />
        </label>
        <label><span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <DatePicker value={dateTo} onChange={setDateTo} data-testid="rekon-akq-date-to"
            className="block mt-1" />
        </label>
        {isRange && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="rekon-akq-reset-range"
            className="h-10 px-3 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
            Reset Range
          </button>
        )}
        <label><span className="text-xs font-medium text-slate-600">Branch</span>
          <select value={branch} onChange={e => setBranch(e.target.value)}
            className="block mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
        </label>
        <button onClick={load} className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-700 inline-flex items-center gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
        <div className="ml-auto grid grid-cols-2 md:grid-cols-4 gap-3 text-right">
          <div><p className="text-xs text-slate-500">Transaksi</p>
            <p className="font-display text-xl md:text-2xl font-bold text-slate-900" data-testid="stat-akq-tx">{items.length}</p></div>
          <div><p className="text-xs text-slate-500">Total Keluar</p>
            <p className="font-display text-xl md:text-2xl font-bold text-violet-700">{idr(totalOut)}</p></div>
          <div><p className="text-xs text-slate-500">Selisih COA</p>
            <p className={`font-display text-xl md:text-2xl font-bold ${Math.abs(coa?.total_selisih || 0) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}
              data-testid="stat-akq-selisih">{idr(coa?.total_selisih || 0)}</p></div>
          <div><p className="text-xs text-slate-500">Net (setelah Penyesuaian)</p>
            <p className={`font-display text-xl md:text-2xl font-bold ${Math.abs(coa?.total_net_selisih || 0) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}
              data-testid="stat-akq-net">{idr(coa?.total_net_selisih || 0)}</p></div>
        </div>
      </div>

      {/* Ringkasan trade-in dari penjualan — dibaca-lewat dari sales, TIDAK ikut
          Total Keluar/Selisih COA di atas (trade-in bukan uang keluar, sudah
          diperhitungkan di jurnal penjualannya sendiri, lihat catatan di rekon.py). */}
      {tiSummary.n_trade_in_unit > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4 flex flex-wrap items-center gap-6"
          data-testid="panel-trade-in-summary">
          <div className="flex items-center gap-2 text-violet-700 shrink-0">
            <ArrowLeftRight className="w-4 h-4" />
            <p className="text-sm font-semibold text-slate-900">Ringkasan Trade-In (dari Penjualan)</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
            <div><p className="text-xs text-slate-500">Unit Trade-In</p>
              <p className="font-display text-lg font-bold text-slate-900" data-testid="stat-ti-unit">{tiSummary.n_trade_in_unit}</p></div>
            <div><p className="text-xs text-slate-500">Belum Diverifikasi</p>
              <p className="font-display text-lg font-bold text-amber-700" data-testid="stat-ti-belum">{tiSummary.n_trade_in_belum_diverifikasi}</p></div>
            <div><p className="text-xs text-slate-500">Sudah Diterima Gudang</p>
              <p className="font-display text-lg font-bold text-emerald-700" data-testid="stat-ti-diterima">{tiSummary.n_trade_in_diterima}</p></div>
            <div><p className="text-xs text-slate-500">Total Nilai Trade-In</p>
              <p className="font-display text-lg font-bold text-violet-700" data-testid="stat-ti-nilai">{idr(tiSummary.total_nilai_trade_in)}</p></div>
          </div>
        </div>
      )}

      {/* Saldo penampung trade-in — ALAT KONTROLNYA. Saldo != 0 berarti ada
          nilai trade-in yang sudah diterima dari pelanggan tapi barangnya belum
          masuk stok lewat PO, ATAU nilai yang masuk berbeda dari taksiran kasir.
          Dua hal berbeda dengan angka yang sama persis — layar hanya menunjukkan
          angkanya, manusia yang memutuskan artinya. Karena itu tidak ada
          penutupan otomatis di sini. */}
      {bolehLihatSaldoTI && saldoTI && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-4"
          data-testid="panel-saldo-penampung-ti">
          <div className="px-4 py-3 border-b border-slate-100 bg-amber-50/40 flex items-center gap-2 flex-wrap">
            <Scale className="w-4 h-4 text-amber-700" />
            <p className="text-sm font-semibold text-slate-900">
              Saldo Penampung Trade-In (belum masuk stok)
            </p>
            <button onClick={loadSaldoTI} data-testid="btn-refresh-saldo-ti"
              className="ml-auto h-8 px-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs inline-flex items-center gap-1.5 text-slate-600">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          {(saldoTI.rows || []).filter(r => r.menggantung).length === 0 ? (
            <p className="px-4 py-4 text-sm text-emerald-700 inline-flex items-center gap-2"
              data-testid="saldo-ti-semua-nol">
              <CheckCircle2 className="w-4 h-4" />
              Semua cabang bersaldo nol — setiap barang trade-in sudah masuk stok dengan nilai yang sesuai.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
                    <th className="text-left px-4 py-2">Akun Penampung</th>
                    <th className="text-right px-4 py-2">Saldo Menggantung</th>
                    <th className="text-left px-4 py-2">Artinya</th>
                    <th className="text-right px-4 py-2">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {(saldoTI.rows || []).filter(r => r.menggantung).map(r => (
                    <Fragment key={r.akun_id}>
                      <tr className="border-t border-slate-100"
                        data-testid={`saldo-ti-row-${r.code}`}>
                        <td className="px-4 py-2">
                          <span className="font-mono text-xs text-slate-500 mr-2">{r.code}</span>
                          <span className="font-medium text-slate-900">{r.name}</span>
                        </td>
                        <td className="px-4 py-2 text-right font-display font-bold text-amber-700">
                          {idr(r.saldo)}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500 max-w-md">
                          {r.darurat ? (
                            <span className="text-rose-700 inline-flex items-start gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                              {r.catatan}
                            </span>
                          ) : (
                            <>Barang belum masuk stok lewat PO, <b>atau</b> nilai yang masuk berbeda dari taksiran kasir.</>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {/* Baris darurat 1055 TIDAK boleh ditutup ke 6195 — itu
                              bukan selisih penilaian, melainkan akun cabang yang
                              tidak ketemu saat posting. Backend juga menolaknya. */}
                          {bolehTutupSelisih && !r.darurat && (
                            <button
                              onClick={() => setSelisihDraft(
                                selisihDraft.akun_id === r.akun_id
                                  ? { akun_id: "", nominal: "", alasan: "" }
                                  : { akun_id: r.akun_id, nominal: Math.abs(r.saldo), alasan: "" })}
                              data-testid={`btn-tutup-selisih-${r.code}`}
                              className="h-8 px-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-xs font-medium">
                              {selisihDraft.akun_id === r.akun_id ? "Batal" : "Tutup Selisih"}
                            </button>
                          )}
                        </td>
                      </tr>
                      {selisihDraft.akun_id === r.akun_id && (
                        <tr className="bg-amber-50/30">
                          <td colSpan={4} className="px-4 py-3">
                            <div className="flex flex-wrap items-end gap-3">
                              <label><span className="text-xs font-medium text-slate-600">Nominal ditutup</span>
                                <InputRupiah value={selisihDraft.nominal}
                                  onChange={v => setSelisihDraft(d => ({ ...d, nominal: v }))}
                                  testid="input-nominal-selisih"
                                  className="block mt-1 h-10 w-44" />
                              </label>
                              <label className="flex-1 min-w-[280px]">
                                <span className="text-xs font-medium text-slate-600">
                                  Alasan (wajib, minimal 10 huruf)
                                </span>
                                <input type="text" value={selisihDraft.alasan}
                                  onChange={e => setSelisihDraft(d => ({ ...d, alasan: e.target.value }))}
                                  placeholder="mis. Taksiran kasir Rp 3jt, hasil cek teknisi Rp 2,5jt (LCD burn-in)"
                                  data-testid="input-alasan-selisih"
                                  className="block mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                              </label>
                              <button onClick={() => simpanSelisih(r)} disabled={busySelisih}
                                data-testid="btn-simpan-selisih"
                                className="h-10 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium">
                                {busySelisih ? "Menyimpan..." : "Simpan ke 6195"}
                              </button>
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                              Tutup ke 6195 <b>hanya</b> kalau barangnya sudah masuk stok dan yang tersisa
                              memang beda nilai. Kalau barangnya belum masuk stok, buat PO-nya dulu —
                              saldo akan turun sendiri.
                            </p>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Daftar tunggu trade-in — barang yang ADA DI LACI tapi belum ada di
          sistem. Ini pasangan layar dari Saldo Penampung di atas: yang satu
          menjawab "berapa nilainya", yang ini menjawab "yang mana, milik siapa,
          sudah berapa lama". Tanpa layar ini barang bisa mengendap berbulan-bulan
          tanpa satu pun tampilan yang menunjukkannya. */}
      {bolehLihatAkuisisi && tunggu && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-4"
          data-testid="panel-daftar-tunggu-ti">
          <div className="px-4 py-3 border-b border-slate-100 bg-amber-50/40 flex items-center gap-2 flex-wrap">
            <Clock className="w-4 h-4 text-amber-700" />
            <p className="text-sm font-semibold text-slate-900">
              Daftar Tunggu Trade-In — belum masuk stok
            </p>
            {tunggu.jumlah_terlambat > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold"
                data-testid="badge-ti-terlambat">
                {tunggu.jumlah_terlambat} terlambat
              </span>
            )}
            <span className="text-xs text-slate-500">
              {(tunggu.rows || []).length} barang · {idr(tunggu.total_nilai || 0)}
            </span>

            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={hanyaTerlambat}
                  onChange={e => setHanyaTerlambat(e.target.checked)}
                  data-testid="cek-hanya-terlambat"
                  className="rounded border-slate-300" />
                Hanya yang terlambat
              </label>

              {/* Ambang hari sengaja bisa diubah dari layar ini, bukan ditanam
                  di kode: 3 hari benar untuk kondisi hari ini, dan kondisi
                  berubah (keputusan owner 24 Agu 2026). */}
              {editAmbang ? (
                <span className="inline-flex items-center gap-1.5">
                  <input type="number" min="0" max="365" value={ambangDraft}
                    onChange={e => setAmbangDraft(e.target.value)}
                    data-testid="input-ambang-hari"
                    className="h-8 w-16 px-2 rounded-lg border border-slate-200 text-xs" />
                  <span className="text-xs text-slate-500">hari</span>
                  <button onClick={simpanAmbang} data-testid="btn-simpan-ambang"
                    className="h-8 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium">
                    Simpan
                  </button>
                  <button onClick={() => { setEditAmbang(false); setAmbangDraft(String(tunggu.ambang_hari)); }}
                    className="h-8 px-2 rounded-lg border border-slate-200 text-xs text-slate-600">
                    Batal
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => canVerifTradeIn && setEditAmbang(true)}
                  disabled={!canVerifTradeIn}
                  data-testid="btn-ubah-ambang"
                  title={canVerifTradeIn ? "Ubah batas hari" : "Hanya bisa diubah oleh yang berhak memverifikasi trade-in"}
                  className="h-8 px-2.5 rounded-lg border border-slate-200 text-xs text-slate-600 inline-flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-default">
                  <Settings2 className="w-3 h-3" /> Terlambat setelah {tunggu.ambang_hari} hari
                </button>
              )}
            </div>
          </div>

          {(tunggu.rows || []).length === 0 ? (
            <p className="px-4 py-4 text-sm text-emerald-700 inline-flex items-center gap-2"
              data-testid="daftar-tunggu-kosong">
              <CheckCircle2 className="w-4 h-4" />
              {hanyaTerlambat
                ? "Tidak ada barang trade-in yang terlambat masuk PO."
                : "Semua barang trade-in sudah masuk stok — tidak ada yang menggantung."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
                    <th className="text-left px-4 py-2">Tanggal</th>
                    <th className="text-left px-4 py-2">Cabang</th>
                    <th className="text-left px-4 py-2">Pelanggan</th>
                    <th className="text-left px-4 py-2">Barang / IMEI</th>
                    <th className="text-right px-4 py-2">Nilai</th>
                    <th className="text-right px-4 py-2">Umur</th>
                    <th className="text-left px-4 py-2">Fisik</th>
                  </tr>
                </thead>
                <tbody>
                  {(tunggu.rows || []).map(r => (
                    <tr key={`${r.sale_id}-${r.trade_in_index}`}
                      data-testid={`tunggu-row-${r.sale_id.slice(0, 8)}-${r.trade_in_index}`}
                      className={`border-t border-slate-100 ${r.terlambat ? "bg-rose-50/50" : ""}`}>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-600">{r.tanggal}</td>
                      <td className="px-4 py-2 text-slate-600">{r.branch || "-"}</td>
                      <td className="px-4 py-2 text-slate-900">{r.nama_pelanggan || "-"}</td>
                      <td className="px-4 py-2">
                        <span className="text-slate-900">{r.nama_unit}</span>
                        {r.imei
                          ? <span className="block font-mono text-xs text-slate-400">{r.imei}</span>
                          : <span className="block text-xs text-amber-700">tanpa IMEI</span>}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-slate-900">{idr(r.nilai)}</td>
                      <td className={`px-4 py-2 text-right font-semibold whitespace-nowrap ${r.terlambat ? "text-rose-700" : "text-slate-600"}`}>
                        {r.umur_hari} hari
                        {r.terlambat && <AlertTriangle className="w-3.5 h-3.5 inline ml-1 mb-0.5" />}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {r.status_fisik === "diterima" ? "sudah diterima gudang" : "belum diverifikasi"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100">
                Barang di sini sudah jadi milik toko tapi belum tercatat sebagai persediaan.
                Masukkan lewat <b>PO yang dibayar dari akun Penerimaan Trade-in</b> cabangnya —
                begitu diterima, barisnya hilang dari daftar ini dan saldo penampung ikut turun.
              </p>
            </div>
          )}
        </div>
      )}

      {/* COA Rekon */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-4" data-testid="coa-panel-akq">
        <div className="px-4 py-3 border-b border-slate-100 bg-violet-50/40 flex items-center gap-2">
          <ArrowDownRight className="w-4 h-4 text-violet-700" />
          <p className="text-sm font-semibold text-slate-900">Rekap COA — Nominal Keluar (Ekspektasi vs Aktual)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
                <SortTh label="Channel (COA)" sortKeyName="channel" sortKey={coaSort.sortKey} sortDir={coaSort.sortDir} onSort={coaSort.toggle} testid="coa-akq-sort-channel" />
                <SortTh label="Ekspektasi" sortKeyName="expected" sortKey={coaSort.sortKey} sortDir={coaSort.sortDir} onSort={coaSort.toggle} align="right" testid="coa-akq-sort-ekspektasi" />
                <th className="text-right py-2 px-3">Aktual Keluar</th>
                <SortTh label="Selisih" sortKeyName="selisih" sortKey={coaSort.sortKey} sortDir={coaSort.sortDir} onSort={coaSort.toggle} align="right" testid="coa-akq-sort-selisih" />
                <th className="text-right py-2 px-3" title="Penyesuaian manual atas selisih">Penyesuaian</th>
                <SortTh label="Net Selisih" sortKeyName="net_selisih" sortKey={coaSort.sortKey} sortDir={coaSort.sortDir} onSort={coaSort.toggle} align="right" testid="coa-akq-sort-net" />
                <th className="text-center py-2 px-3">Status</th>
                {canAdjust && <th className="text-center py-2 px-3">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {activeCoa.length === 0 && (
                <tr><td colSpan={canAdjust ? 8 : 7} className="text-center py-10 text-slate-400">
                  <ShoppingBag className="w-8 h-8 text-slate-300 mx-auto mb-2" />Belum ada akuisisi untuk {date}
                </td></tr>
              )}
              {sortRows(activeCoa, coaSort.sortKey, coaSort.sortDir).map(r => (
                <tr key={r.channel} className="border-b border-slate-100" data-testid={`coa-row-akq-${r.channel}`}>
                  <td className="py-2 px-3 font-medium">{r.channel}</td>
                  <td className="py-2 px-3 text-right font-mono">{idr(r.expected)}</td>
                  <td className="py-2 px-3 text-right">
                    {canAdjust ? (
                      <InputRupiah value={actualDraft[r.channel] ?? ""}
                        onChange={n => setActualDraft(d => ({ ...d, [r.channel]: n }))}
                        testid={`coa-actual-akq-${r.channel}`}
                        className="h-8 px-2 rounded border border-slate-200 text-xs text-right font-mono w-32" />
                    ) : <span className="font-mono">{idr(r.actual)}</span>}
                  </td>
                  <td className={`py-2 px-3 text-right font-mono ${Math.abs(r.selisih) < 0.5 ? "text-slate-500" : "text-rose-700 font-semibold"}`}>
                    {r.selisih === 0 ? "-" : idr(r.selisih)}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {canAdjust ? (
                      <div className="flex flex-col items-end gap-1">
                        <input type="number" value={adjDraft[r.channel] ?? ""}
                          onChange={e => setAdjDraft(d => ({ ...d, [r.channel]: e.target.value }))}
                          data-testid={`coa-adj-akq-${r.channel}`}
                          placeholder="0"
                          className="h-8 px-2 rounded border border-amber-200 bg-amber-50/40 text-xs text-right font-mono w-28" />
                        <input type="text" value={adjNoteDraft[r.channel] ?? ""}
                          onChange={e => setAdjNoteDraft(d => ({ ...d, [r.channel]: e.target.value }))}
                          data-testid={`coa-adj-note-akq-${r.channel}`}
                          placeholder="ket. penyesuaian"
                          className="h-7 px-2 rounded border border-slate-200 text-[10px] text-right w-28 italic text-slate-500" />
                      </div>
                    ) : <span className="font-mono">{idr(r.adjustment || 0)}</span>}
                  </td>
                  <td className={`py-2 px-3 text-right font-mono font-bold ${Math.abs(r.net_selisih || 0) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}>
                    {(r.net_selisih || 0) === 0 ? "-" : idr(r.net_selisih || 0)}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {r.status === "VALID" ? (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> VALID
                      </span>
                    ) : r.status === "CEK" ? (
                      <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-xs font-bold inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> CEK
                      </span>
                    ) : <span className="text-xs text-slate-400">-</span>}
                  </td>
                  {canAdjust && (
                    <td className="py-2 px-3 text-center">
                      <button onClick={() => saveActual(r.channel)}
                        data-testid={`btn-save-coa-akq-${r.channel}`}
                        className="text-xs px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium">Simpan</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {coa && (coa.total_expected > 0 || coa.total_actual > 0) && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td className="py-2 px-3 font-bold text-slate-700">TOTAL</td>
                  <td className="py-2 px-3 text-right font-mono font-bold">{idr(coa.total_expected)}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold">{idr(coa.total_actual)}</td>
                  <td className={`py-2 px-3 text-right font-mono font-bold ${Math.abs(coa.total_selisih) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}>{idr(coa.total_selisih)}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold text-amber-700">{idr(coa.total_adjustment || 0)}</td>
                  <td className={`py-2 px-3 text-right font-mono font-bold ${Math.abs(coa.total_net_selisih || 0) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}>{idr(coa.total_net_selisih || 0)}</td>
                  <td colSpan={canAdjust ? 2 : 1}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Akuisisi list */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-sm font-semibold text-slate-900">Transaksi Akuisisi ({items.length})</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
              <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={txSort.sortKey} sortDir={txSort.sortDir} onSort={txSort.toggle} testid="akq-tx-sort-tanggal" />
              <SortTh label="Pelanggan" sortKeyName="nama_pelanggan" sortKey={txSort.sortKey} sortDir={txSort.sortDir} onSort={txSort.toggle} testid="akq-tx-sort-pelanggan" />
              <th className="text-left py-2 px-3">Unit</th>
              <SortTh label="Harga" sortKeyName="harga_akuisisi" sortKey={txSort.sortKey} sortDir={txSort.sortDir} onSort={txSort.toggle} align="right" testid="akq-tx-sort-harga" />
              <th className="text-right py-2 px-3" title="Nilai aktual yang dibayarkan per channel (input manual). Tidak berlaku untuk baris Trade-In — lihat kolom Verifikasi TI.">Nilai Aktual</th>
              <SortTh label="Selisih Tx" sortKeyName="selisih_tx" sortKey={txSort.sortKey} sortDir={txSort.sortDir} onSort={txSort.toggle} align="right" testid="akq-tx-sort-selisih" />
              <SortTh label="Metode" sortKeyName="metode_pembayaran" sortKey={txSort.sortKey} sortDir={txSort.sortDir} onSort={txSort.toggle} testid="akq-tx-sort-metode" />
              <SortTh label="Penerima" sortKeyName="nama_penerima" sortKey={txSort.sortKey} sortDir={txSort.sortDir} onSort={txSort.toggle} testid="akq-tx-sort-penerima" />
              <th className="text-center py-2 px-3">QC</th>
              <th className="text-center py-2 px-3" title="Verifikasi fisik unit trade-in oleh gudang — hanya berlaku untuk baris Trade-In">Verifikasi TI</th>
            </tr>
          </thead>
          <tbody>
            {sortRows(items, txSort.sortKey, txSort.sortDir).map(a => {
              const selTx = a.selisih_tx;
              const isTradeIn = a.sumber === "trade_in";
              // `a.trade_in` (poin 7 owner): jenis akuisisi ANDAL untuk baris
              // `sumber === "akuisisi"` sendiri — beda dari `isTradeIn` di atas,
              // yang menandai baris trade-in PENJUALAN (dibaca-lewat dari
              // sales.trade_in_items, bukan dokumen acquisitions). Field ini
              // sudah dikirim GET /rekon-akuisisi sejak sebelumnya (rekon.py),
              // baru sekarang ditampilkan.
              const isAuTradeIn = a.sumber === "akuisisi" && !!a.trade_in;
              // Baris trade-in menuju PENJUALAN asalnya (dokumen akuisisi memang
              // sengaja tidak dibuat — lihat catatan panjang di rekon.py); baris
              // akuisisi biasa menuju halaman Akuisisi. Dua tujuan BERBEDA — jangan
              // disatukan (poin 4 brief bos 14 Agu).
              const invoiceHref = isTradeIn ? `/sales?id=${encodeURIComponent(a.sale_id)}` : `/acquisitions?id=${encodeURIComponent(a.id)}`;
              const invoiceId = isTradeIn ? a.sale_id : a.id;
              const bolehLihatInvoice = isTradeIn ? bolehLihatSales : bolehLihatAkuisisi;
              return (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`akq-row-${a.id}`}>
                  <td className="py-2 px-3 text-xs">
                    <div className="text-slate-700 font-medium">{a.tanggal || "-"}</div>
                    <div className="text-slate-400">{a.waktu || "-"}</div>
                  </td>
                  <td className="py-2 px-3">{a.nama_pelanggan}</td>
                  <td className="py-2 px-3">
                    <button type="button" onClick={() => openDetail(a)} data-testid={`akq-detail-btn-${a.id}`}
                      className="text-left hover:underline decoration-dotted decoration-slate-400">
                      {a.type_hp || a.nama_unit}
                    </button>
                    {isTradeIn && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 font-bold">TI</span>}
                    {isAuTradeIn && (
                      <span data-testid={`akq-jenis-tradein-${a.id}`}
                        className="ml-1 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 font-bold">
                        AU-TRADE-IN
                      </span>
                    )}
                    {/* Nomor invoice → TAB BARU (poin C brief bos 14 Agu) */}
                    <div className="mt-0.5">
                      {bolehLihatInvoice ? (
                        <a href={invoiceHref} target="_blank" rel="noreferrer"
                          data-testid={`akq-invoice-link-${a.id}`}
                          className="inline-flex items-center gap-0.5 text-[10px] font-mono text-sky-700 hover:underline">
                          {noInvoice(invoiceId)}
                        </a>
                      ) : (
                        <span className="text-[10px] font-mono text-slate-400" data-testid={`akq-invoice-${a.id}`}>
                          {noInvoice(invoiceId)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-mono font-semibold text-violet-800">{idr(a.harga_akuisisi)}</td>
                  <td className="py-2 px-3 text-right">
                    {isTradeIn ? (
                      // Trade-in bukan uang keluar per channel pembayaran — tidak ada
                      // "nilai aktual" untuk direkonsiliasi di sini (backend menolak
                      // tx_key berawalan "tradein:" di /rekon-tx-actuals dengan 400).
                      // Verifikasinya lewat kolom Verifikasi TI, bukan input nominal.
                      <span className="font-mono text-xs text-slate-300" title="Trade-in tidak direkonsiliasi per channel pembayaran">n/a</span>
                    ) : canAdjust ? (
                      <InputRupiah value={txActualDraft[a.tx_key] ?? ""}
                        onChange={n => setTxActualDraft(d => ({ ...d, [a.tx_key]: n }))}
                        onBlur={() => {
                          const v = parseFloat(txActualDraft[a.tx_key]);
                          if (!isNaN(v) && v !== (a.actual_value ?? -1)) saveTxActual(a.tx_key);
                        }}
                        testid={`tx-actual-akq-${a.id}`}
                        placeholder="—"
                        className="h-8 px-2 rounded border border-violet-200 bg-violet-50/30 text-xs text-right font-mono w-28" />
                    ) : (
                      <span className="font-mono text-xs">{a.actual_value != null ? idr(a.actual_value) : "-"}</span>
                    )}
                  </td>
                  <td className={`py-2 px-3 text-right font-mono text-xs ${selTx == null ? "text-slate-300" : Math.abs(selTx) < 0.5 ? "text-emerald-700 font-semibold" : "text-rose-700 font-bold"}`}>
                    {selTx == null ? "-" : (selTx === 0 ? "0" : idr(selTx))}
                  </td>
                  <td className="py-2 px-3 text-xs">{a.metode_pembayaran || "-"}</td>
                  <td className="py-2 px-3 text-xs">{a.nama_penerima || "-"}</td>
                  <td className="py-2 px-3 text-center">
                    {isTradeIn ? (
                      <span className="text-xs text-slate-300">-</span>
                    ) : (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${a.qc_check === "Passed" ? "bg-emerald-100 text-emerald-800" : a.qc_check === "Failed" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{a.qc_check || "-"}</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center" data-testid={`ti-verif-${a.id}`}>
                    {!isTradeIn ? (
                      <span className="text-xs text-slate-300">-</span>
                    ) : a.status_penerimaan === "diterima" ? (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold inline-flex items-center gap-1"
                          title={`Diterima oleh ${a.penerimaan?.diterima_oleh || "-"}${a.penerimaan?.diterima_at ? " pada " + new Date(a.penerimaan.diterima_at).toLocaleString("id-ID") : ""}`}>
                          <CheckCircle2 className="w-3 h-3" /> Diterima
                        </span>
                        {canVerifTradeIn && (
                          <button onClick={() => batalTradeIn(a)}
                            data-testid={`btn-batal-ti-${a.sale_id}-${a.trade_in_index}`}
                            className="text-[10px] text-rose-600 hover:underline">Batalkan</button>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${a.status_penerimaan === "dibatalkan" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-800"}`}>
                          {a.status_penerimaan === "dibatalkan" ? "Dibatalkan" : "Belum Diterima"}
                        </span>
                        {canVerifTradeIn && (
                          <button onClick={() => terimaTradeIn(a)}
                            data-testid={`btn-terima-ti-${a.sale_id}-${a.trade_in_index}`}
                            className="text-[10px] text-emerald-700 hover:underline font-medium">Tandai Diterima</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* DETAIL MODAL (poin C brief bos 14 Agu) — dua bentuk isi tergantung
          `detail._tipe`: "akuisisi" (fetch penuh) atau "trade_in" (baca-lewat
          dari sales, data sudah lengkap di baris listing). */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}
            data-testid="akq-rekon-detail-modal">
            <div className="sticky top-0 bg-violet-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                <h3 className="font-display font-semibold">
                  {detail._tipe === "trade_in" ? "Detail Trade-in (dari Penjualan)" : "Detail Akuisisi"}
                </h3>
              </div>
              <button onClick={() => setDetail(null)} data-testid="akq-rekon-detail-close"
                className="p-2 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            {detail._loading ? (
              <div className="p-6 text-sm text-slate-500 text-center">Memuat...</div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="flex items-start justify-between border-b border-slate-100 pb-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Pelanggan</p>
                    <p className="font-display text-lg font-bold text-slate-900">{detail.nama_pelanggan}</p>
                    {detail._tipe === "akuisisi" && (
                      <p className="text-sm text-slate-500 mt-1"><Phone className="w-3.5 h-3.5 inline mr-1" />{detail.no_hp || "-"}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Tanggal</p>
                    <p className="font-mono text-sm text-slate-900">{detail.tanggal}{detail.waktu ? ` · ${detail.waktu}` : ""}</p>
                    <p className="text-xs text-violet-700 font-medium mt-1">{detail.branch || "-"}</p>
                  </div>
                </div>

                {/* Nomor invoice → TAB BARU (poin C). Trade-in mengarah ke penjualan
                    asalnya, akuisisi biasa mengarah ke halaman Akuisisi. */}
                {(() => {
                  const isTi = detail._tipe === "trade_in";
                  const href = isTi ? `/sales?id=${encodeURIComponent(detail.sale_id)}` : `/acquisitions?id=${encodeURIComponent(detail.id)}`;
                  const invId = isTi ? detail.sale_id : detail.id;
                  const boleh = isTi ? bolehLihatSales : bolehLihatAkuisisi;
                  return boleh ? (
                    <a href={href} target="_blank" rel="noreferrer" data-testid="akq-rekon-detail-invoice-link"
                      className="inline-flex items-center gap-1.5 text-sm font-mono text-sky-700 hover:underline">
                      <FileText className="w-3.5 h-3.5" /> {noInvoice(invId)} — buka {isTi ? "rincian penjualan" : "transaksi akuisisi"} di tab baru
                    </a>
                  ) : (
                    <p className="text-xs font-mono text-slate-400">{noInvoice(invId)}</p>
                  );
                })()}

                {detail._tipe === "trade_in" ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <Field label="Nama Unit"><span className="font-semibold">{detail.nama_unit || "-"}</span></Field>
                    <Field label="Nilai Trade-in"><span className="font-mono font-semibold">{idr(detail.harga_akuisisi)}</span></Field>
                    <Field label="IMEI"><span className="font-mono">{detail.imei || "-"}</span></Field>
                    <Field label="Penjual (kasir)">{detail.nama_penjual || "-"}</Field>
                    <Field label="Status Penerimaan Gudang">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold w-fit ${detail.status_penerimaan === "diterima" ? "bg-emerald-100 text-emerald-800" : detail.status_penerimaan === "dibatalkan" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-800"}`}>
                        {detail.status_penerimaan === "diterima" ? "Diterima" : detail.status_penerimaan === "dibatalkan" ? "Dibatalkan" : "Belum Diterima"}
                      </span>
                    </Field>
                    {detail.penerimaan?.diterima_oleh && (
                      <Field label="Diterima Oleh">{detail.penerimaan.diterima_oleh}</Field>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <Field label="Type HP"><span className="font-semibold">{detail.type_hp}</span></Field>
                    <Field label="Harga Akuisisi"><span className="font-mono font-semibold">{idr(detail.harga_akuisisi)}</span></Field>
                    <Field label="Jenis Akuisisi">
                      {detail.trade_in ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold w-fit">
                          <ArrowLeftRight className="w-3.5 h-3.5" /> AU (Trade-in)
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold w-fit">AU (Terima Jual)</span>
                      )}
                    </Field>
                    <Field label="IMEI"><span className="font-mono">{detail.imei || "-"}</span></Field>
                    <Field label="Storage · Grade">{(detail.storage || "-") + " · " + (detail.grade || "-")}</Field>
                    <Field label="Rekening"><span className="font-mono">{detail.no_rekening || "-"}</span></Field>
                    <Field label="Nama Bank"><span className="inline-flex items-center gap-1"><Building className="w-3.5 h-3.5 text-slate-400" />{detail.nama_bank || "-"}</span></Field>
                    <Field label="QC Check">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium w-fit ${detail.qc_check === "Passed" ? "bg-emerald-100 text-emerald-800" : detail.qc_check === "Failed" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                        {detail.qc_check}
                      </span>
                    </Field>
                    <Field label="Penerima">{detail.nama_penerima}</Field>
                    <Field label="Catatan QC"><span className="text-slate-600">{detail.qc_notes || "-"}</span></Field>
                    <Field label="Catatan"><span className="text-slate-600 italic">{detail.catatan || "-"}</span></Field>
                  </div>
                )}

                {detail._tipe === "akuisisi" && (detail.trade_in_items || []).length > 0 && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-violet-800 flex items-center gap-1.5 mb-1">
                      <ArrowLeftRight className="w-3.5 h-3.5" /> Unit Trade-in yang diberikan
                    </p>
                    <ul className="text-xs text-violet-900/90 space-y-0.5">
                      {detail.trade_in_items.map((t, i) => (
                        <li key={i}>· {t.nama || "(tanpa nama)"} {t.imei ? `— IMEI ${t.imei}` : ""} — <b>{idr(t.harga)}</b></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Read-only label+value pair — dipakai DETAIL MODAL di atas (pola sama
// dengan `Field` di Acquisitions.jsx/RekonSales.jsx, diduplikasi di sini
// karena bukan komponen bersama — lihat batas berkas brief ini).
function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm text-slate-900">{children}</div>
    </div>
  );
}
